// ============================================================
// FILE: PhotoUploader.js
// LOCATION: C:\gardenmarket\web\src\components\PhotoUploader.js
// ============================================================
// PURPOSE:
//   Shared photo upload widget — a file picker + thumbnail grid
//   with per-photo delete and click-to-enlarge lightbox. Used by:
//     - PostJob.js (buyer, photo_type='job_posting')
//     - JobDetailContent.js / BookingDetailContent.js (gardener,
//       photo_type='before' or 'after', completion photos)
//     - GardenerProfile.js (portfolio uploads, no job_id)
//
// CHANGE (this version): added a lightbox so clicking any
//   thumbnail shows the full-size image in an overlay. Clicking
//   the overlay background or the X button closes it. Keyboard
//   ESC also closes it for accessibility.
//
// PROPS:
//   uploadUrl      — full API path to POST files to
//   extraFields    — object merged into multipart form data
//   existingPhotos — array of { photo_id|portfolio_photo_id,
//                    storage_url, caption } already saved
//   onDeleteUrl    — function(photo) => DELETE API path
//   onChange       — called after any upload or delete
//   maxFiles       — defaults to 9
//   label          — heading text shown above the control
// ============================================================

import { useState, useRef, useEffect } from 'react';
import client from '../api/client';

const API_BASE = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:4000';

export default function PhotoUploader({
    uploadUrl,
    extraFields = {},
    existingPhotos = [],
    onDeleteUrl,
    onChange,
    maxFiles = 9,
    label = 'Photos',
}) {
    const [uploading,    setUploading]    = useState(false);
    const [error,        setError]        = useState('');
    const [lightboxPhoto, setLightboxPhoto] = useState(null); // photo object or null
    const fileInputRef = useRef(null);

    // Close lightbox on ESC key
    useEffect(() => {
        const handleKey = (e) => {
            if (e.key === 'Escape') setLightboxPhoto(null);
        };
        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, []);

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length === 0) return;

        if (existingPhotos.length + files.length > maxFiles) {
            setError(`You can have at most ${maxFiles} photos here.`);
            return;
        }

        setError('');
        setUploading(true);

        const formData = new FormData();
        files.forEach(file => formData.append('photos', file));
        Object.entries(extraFields).forEach(([key, val]) => formData.append(key, val));

        try {
            await client.post(uploadUrl, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (onChange) onChange();
        } catch (err) {
            setError(err.response?.data?.error || 'Upload failed.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (e, photo) => {
        e.stopPropagation(); // don't open lightbox when deleting
        if (!onDeleteUrl) return;
        try {
            await client.delete(onDeleteUrl(photo));
            if (onChange) onChange();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete photo.');
        }
    };

    return (
        <div>
            <div style={styles.headerRow}>
                <label style={styles.label}>{label}</label>
                <span style={styles.count}>{existingPhotos.length}/{maxFiles}</span>
            </div>

            {error && <div style={styles.error}>{error}</div>}

            <div style={styles.grid}>
                {existingPhotos.map(photo => {
                    const photoId = photo.photo_id || photo.portfolio_photo_id;
                    const src = `${API_BASE}${photo.storage_url}`;
                    return (
                        <div
                            key={photoId}
                            style={styles.thumbWrap}
                            onClick={() => setLightboxPhoto(photo)}
                            title="Click to enlarge"
                        >
                            <img
                                src={src}
                                alt={photo.caption || 'Uploaded photo'}
                                style={styles.thumb}
                                onError={e => { e.target.style.display = 'none'; }}
                            />
                            {/* Hover hint overlay */}
                            <div style={styles.hoverHint}>🔍</div>

                            {onDeleteUrl && (
                                <button
                                    onClick={(e) => handleDelete(e, photo)}
                                    style={styles.deleteBtn}
                                    title="Remove photo"
                                    type="button"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                    );
                })}

                {existingPhotos.length < maxFiles && (
                    <label style={styles.addTile}>
                        {uploading ? 'Uploading...' : '+ Add'}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/jpeg,image/png,image/heic,image/webp"
                            multiple
                            onChange={handleFileSelect}
                            disabled={uploading}
                            style={{ display: 'none' }}
                        />
                    </label>
                )}
            </div>

            {/* ── Lightbox ──────────────────────────────────────────
                Renders as a full-screen overlay when a photo is
                clicked. Click the backdrop or the X to close.
                The image itself is constrained to 90% viewport so
                even large photos don't overflow on small screens. */}
            {lightboxPhoto && (
                <div
                    style={styles.lightboxBackdrop}
                    onClick={() => setLightboxPhoto(null)}
                >
                    <div
                        style={styles.lightboxContent}
                        onClick={e => e.stopPropagation()} // don't close when clicking the image
                    >
                        <button
                            onClick={() => setLightboxPhoto(null)}
                            style={styles.lightboxClose}
                            title="Close"
                            type="button"
                        >
                            ×
                        </button>
                        <img
                            src={`${API_BASE}${lightboxPhoto.storage_url}`}
                            alt={lightboxPhoto.caption || 'Full size photo'}
                            style={styles.lightboxImg}
                        />
                        {lightboxPhoto.caption && (
                            <p style={styles.lightboxCaption}>{lightboxPhoto.caption}</p>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

const styles = {
    headerRow:       { display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:'8px' },
    label:           { fontWeight:600, fontSize:'0.85rem', color:'#333' },
    count:           { fontSize:'0.75rem', color:'#999' },
    error:           { backgroundColor:'#fdecea', border:'1px solid #f5c2bb', borderRadius:'6px', padding:'8px 12px', color:'#c0392b', marginBottom:'10px', fontSize:'0.82rem' },
    grid:            { display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(84px, 1fr))', gap:'8px' },

    thumbWrap:       { position:'relative', aspectRatio:'1', borderRadius:'8px', overflow:'hidden', backgroundColor:'#f5f5f5', cursor:'pointer' },
    thumb:           { width:'100%', height:'100%', objectFit:'cover', display:'block', transition:'transform 0.15s ease' },
    hoverHint:       { position:'absolute', inset:0, display:'flex', alignItems:'center', justifyContent:'center', backgroundColor:'rgba(0,0,0,0)', fontSize:'1.2rem', opacity:0, transition:'opacity 0.15s ease',
                       // CSS hover handled inline — workaround since inline styles can't do :hover
                       // We'll use CSS in a <style> tag appended once
                     },
    deleteBtn:       { position:'absolute', top:'4px', right:'4px', width:'22px', height:'22px', borderRadius:'50%', border:'none', backgroundColor:'rgba(0,0,0,0.65)', color:'white', fontSize:'1rem', lineHeight:1, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:2 },
    addTile:         { aspectRatio:'1', borderRadius:'8px', border:'1.5px dashed #ccc', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'0.78rem', color:'#888', cursor:'pointer', textAlign:'center', padding:'4px' },

    lightboxBackdrop:{ position:'fixed', inset:0, backgroundColor:'rgba(0,0,0,0.82)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px' },
    lightboxContent: { position:'relative', maxWidth:'90vw', maxHeight:'90vh', display:'flex', flexDirection:'column', alignItems:'center' },
    lightboxClose:   { position:'absolute', top:'-40px', right:0, background:'none', border:'none', color:'white', fontSize:'2rem', cursor:'pointer', lineHeight:1, padding:'4px 10px' },
    lightboxImg:     { maxWidth:'90vw', maxHeight:'80vh', objectFit:'contain', borderRadius:'8px', boxShadow:'0 8px 32px rgba(0,0,0,0.5)' },
    lightboxCaption: { color:'rgba(255,255,255,0.8)', fontSize:'0.88rem', marginTop:'12px', textAlign:'center' },
};
